import { DEFAULT_PAGE_SIZE } from '../constants/pagination'
import { ApiResponse, MailAccount } from '../types'
import { apiService } from './api.service'

class MailAccountService {
  async getMailAccounts(params?: { page?: number; page_size?: number; full_perms?: boolean }): Promise<ApiResponse<MailAccount>> {
    const response = await apiService.get<ApiResponse<MailAccount>>('mail_accounts/', { params })
    return response.data
  }

  async getMailAccount(id: number): Promise<MailAccount> {
    const response = await apiService.get<MailAccount>(`mail_accounts/${id}/`)
    return response.data
  }

  async createMailAccount(data: Partial<MailAccount>): Promise<MailAccount> {
    const response = await apiService.post<MailAccount>('mail_accounts/', data)
    return response.data
  }

  async updateMailAccount(id: number, data: Partial<MailAccount>): Promise<MailAccount> {
    // Remove expiration from the object before updating
    const { expiration, ...updateData } = data
    const response = await apiService.patch<MailAccount>(`mail_accounts/${id}/`, updateData)
    return response.data
  }

  async deleteMailAccount(id: number): Promise<void> {
    await apiService.delete(`mail_accounts/${id}/`)
  }

  async testMailAccount(account: MailAccount): Promise<any> {
    const testAccount = { ...account }
    delete (testAccount as any).set_permissions
    const response = await apiService.post(`mail_accounts/test/`, testAccount)
    return response.data
  }

  async processMailAccount(id: number): Promise<any> {
    const response = await apiService.post(`mail_accounts/${id}/process/`, {})
    return response.data
  }

  // Methods for ManagementList compatibility
  async listAll(params?: any): Promise<ApiResponse<MailAccount>> {
    return this.getMailAccounts(params)
  }

  async listFiltered(
    page: number,
    _ordering?: string,
    sortField?: string,
    sortReverse?: boolean,
    nameFilter?: string,
    _includeDocumentCount?: boolean,
    extraParams?: any
  ): Promise<ApiResponse<MailAccount>> {
    const params: any = {
      page,
      page_size: extraParams?.page_size ?? DEFAULT_PAGE_SIZE,
      full_perms: true,
    }

    if (sortField) {
      const prefix = sortReverse ? '-' : ''
      params.ordering = `${prefix}${sortField}`
    }

    if (nameFilter) {
      params.name__icontains = nameFilter
    }

    if (extraParams) {
      Object.assign(params, extraParams)
    }

    return this.getMailAccounts(params)
  }

  async delete(account: MailAccount): Promise<void> {
    return this.deleteMailAccount(account.id)
  }

  // Alias methods for EditDialog compatibility
  async create(data: Partial<MailAccount>): Promise<MailAccount> {
    return this.createMailAccount(data)
  }

  async update(id: number, data: Partial<MailAccount>): Promise<MailAccount> {
    return this.updateMailAccount(id, data)
  }

  // Bulk edit for permissions
  async bulkEditObjects(objects: number[], operation: string, permissions?: any, merge?: boolean): Promise<void> {
    const params: any = {
      objects,
      object_type: 'mail_accounts',
      operation,
    }

    if (operation === 'set_permissions' && permissions) {
      params.owner = permissions.owner
      params.permissions = permissions.set_permissions
      params.merge = merge
    }

    await apiService.post('bulk_edit_objects/', params)
  }
}

export const mailAccountService = new MailAccountService()
